/**
 * Zod input schemas: primitives. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
import { PAYOUT_CURRENCIES, EMPLOYMENT_TYPES, ELIGIBILITY_TYPES, USER_ROLES, STATEMENT_STATUSES, EXPENSE_TYPES, FX_RATE_SOURCES, FX_RATE_STATUSES, TRAVEL_DAY_TYPES } from '../enums.js';
// ===========================================================================
// PRIMITIVES
// ===========================================================================
/**
 * Email — must be valid format. We do NOT enforce daaily.com here because
 * schemas are also used to validate inbound data shapes from sources where
 * cross-domain emails could legitimately appear (e.g. a CSV row for a new
 * hire whose Workspace account is still being provisioned). Domain
 * enforcement happens at the auth + Firestore-rules layer instead.
 */
export const EmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email('Must be a valid email address');
/** ISO 4217 currency code — uppercase 3 letters. */
export const Iso4217CodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Must be a 3-letter ISO 4217 currency code');
/** ISO 3166 alpha-2 country code — uppercase 2 letters. */
export const Iso3166CodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Must be a 2-letter ISO 3166 alpha-2 code');
/** ISO 8601 date string YYYY-MM-DD. */
export const IsoDateStringSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be in YYYY-MM-DD format');
/**
 * Money amounts: positive, finite, ≤ 1,000,000. Larger amounts are almost
 * certainly entry errors at this scale, so we surface them as validation
 * errors rather than silently accepting them.
 */
export const MoneyAmountSchema = z
    .number()
    .finite()
    .nonnegative('Amount cannot be negative')
    .max(1_000_000, 'Amount exceeds reasonable limit (1,000,000)');
/**
 * FX rate: positive, finite, ≤ 100,000. ECB rates rarely exceed ~10,000
 * (e.g. JPY); exceeding this signals a misuse of the unit (per-100 vs per-1).
 */
export const FxRateSchema = z
    .number()
    .finite()
    .positive('FX rate must be positive')
    .max(100_000, 'FX rate exceeds reasonable limit');
/**
 * Statement names, business reasons, comments — shown in PDFs and emails.
 * Sane bounds prevent abusive inputs and accidental pasting of large blobs.
 */
export const ShortTextSchema = z
    .string()
    .trim()
    .min(1, 'Cannot be empty')
    .max(200, 'Cannot exceed 200 characters');
export const LongTextSchema = z
    .string()
    .trim()
    .min(1, 'Cannot be empty')
    .max(2000, 'Cannot exceed 2000 characters');
/** Optional long text — empty string and undefined both normalize to null. */
export const OptionalLongTextSchema = z
    .union([z.string().trim().max(2000), z.null(), z.undefined()])
    .transform((v) => (v == null || v === '' ? null : v));
export const MonthSchema = z.number().int().min(1).max(12);
/** Year: bounded to a sane window. Allows previous year (per spec) and a
 *  one-year forward buffer for end-of-year submissions filed in early Jan. */
export const YearSchema = z
    .number()
    .int()
    .min(2024, 'Year is too far in the past')
    .max(new Date().getUTCFullYear() + 1, 'Year is too far in the future');
// ===========================================================================
// ENUMS — derived from the canonical const tuples in /shared/enums.ts
// ===========================================================================
export const PayoutCurrencySchema = z.enum(PAYOUT_CURRENCIES);
export const EmploymentTypeSchema = z.enum(EMPLOYMENT_TYPES);
export const EligibilitySchema = z.enum(ELIGIBILITY_TYPES);
export const UserRoleSchema = z.enum(USER_ROLES);
export const StatementStatusSchema = z.enum(STATEMENT_STATUSES);
export const ExpenseTypeSchema = z.enum(EXPENSE_TYPES);
export const FxRateSourceSchema = z.enum(FX_RATE_SOURCES);
export const FxRateStatusSchema = z.enum(FX_RATE_STATUSES);
export const TravelDayTypeSchema = z.enum(TRAVEL_DAY_TYPES);
//# sourceMappingURL=primitives.js.map